import { useEffect, useMemo, useState } from "react";
import type { DraftableOptions, } from "./useDraftable";

export declare type SharedDraftableOptions = DraftableOptions & {
  instanceId: string;
}

export declare type SharedDraftableUnreadyResponse = {
	draft: undefined;
	keyIsDirty: undefined;
	getChangedPartial: undefined;
	setDraftKey: undefined;
}

export declare type SharedDraftableResult<T extends Record<string, unknown>> = {
	draft: T,
	keyIsDirty: (key: keyof T) => boolean,
	getChangedPartial: () => Partial<T>,
	setDraftKey: (key: keyof T, value: T[keyof T]) => void,
}

// Global store for shared draft instances
// biome-ignore lint/suspicious/noExplicitAny: deal with this later.
const sharedDraftStore = new Map<string, any>();



/**
 * A hook that provides shared draft functionality for a Record/key-value object.
 * Multiple components can use this hook with the same instanceId to share draft state.
 *
 * Usage #1 (draft with object): useSharedDraftable<T>(stored, options)
 * Usage #2 (empty generic draft): useSharedDraftable(false)
 *
 * @template T - The type of the stored object, must be a record with string keys
 * @param stored - The original stored object to create a draft from, or 'false' for uninitialized
 * @param options - Configuration options for the shared draft behavior (not required if stored === false)
 */
export function useSharedDraftable<T extends Record<string, unknown>>(stored: T, options: SharedDraftableOptions): SharedDraftableResult<T>;
export function useSharedDraftable(stored: false): SharedDraftableUnreadyResponse;
export function useSharedDraftable<T extends Record<string, unknown>>(
	stored: T | false,
	options: SharedDraftableOptions = { instanceId: "NONE", emptyValues: false }
) {

	const { instanceId, emptyValues = false } = options;


  const sharedInstance = useMemo(() => {
		if(instanceId === "NONE" || stored === false) {
			return {
				state: stored,
				subscribers: new Set<() => void>(),
			};
		}
		if(!sharedDraftStore.has(instanceId)) {
      const initialState = Object.assign({}, emptyValues ? emptyStoredValues<T>(stored) : stored);
      sharedDraftStore.set(instanceId, {
        state: initialState,
        subscribers: new Set<() => void>()
      });
    }
		const result = sharedDraftStore.get(instanceId);
		if(!result) {
			throw new Error(`useSharedDraftable:: sharedInstance not found for instanceId: ${instanceId}`);
		}
		return result;
	}, [instanceId, emptyValues, stored]);

  const [draft, setDraft] = useState<T>(sharedInstance.state);

	useEffect(() => {
		const updateLocalState = () => setDraft(sharedInstance.state);
		sharedInstance.subscribers.add(updateLocalState);
		return () => {
			sharedInstance.subscribers.delete(updateLocalState);
		};
	}, [sharedInstance]);

  useEffect(() => {
    setDraft(sharedInstance.state);
  }, [sharedInstance]);


  const updateSharedState = (newState: T) => {
    sharedInstance.state = newState;
		sharedInstance.subscribers.forEach((subscriber: () => void) => { subscriber(); });
  };

	if(stored === false) {
		return {
			draft: undefined,
			keyIsDirty: undefined,
			getChangedPartial: undefined,
			setDraftKey: undefined,
		};
	}

	return {
    draft,
    keyIsDirty: (key: keyof T) => [stored[key], "", 255].includes(draft[key]),
    getChangedPartial: (): Partial<T> => {
      return Object.keys(stored).reduce((acc, key) => {
        if(draft[key] !== "" && draft[key] !== -255) {
          //@ts-ignore - fix this later.
          acc[key] = draft[key];
        }
        return acc;
      }, {} as Partial<T>);
    },
    setDraftKey: (key: keyof T, value: T[keyof T]) => {
      const newState = Object.assign({}, draft, { [key]: value });
      updateSharedState(newState);
    },
  }
}


const emptyStoredValues = <T extends Record<string, unknown>>(stored: T) => {
  return Object.keys(stored).reduce((acc, key) => {
    //@ts-ignore - fix this later.
    acc[key] = typeof stored[key] === "number" ? -255 : "";
    return acc;
  }, {} as T);
}
